import { getPositionRelativeToPositionedAncestor } from '@bpm-web-app/utils';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import styles from './sort-button.module.css';
import { ReactComponent as SortIcon } from '../../../assets/icons/sort.svg';
import { AnySortingKey, SortOption } from '../../sort-options-sheet/sort-options-sheet';

interface SortButtonProps {
    componentClass?: string;
    isSortVisible?: boolean;
    setSortPosition: (position: { left: number, top: number }) => void;
    setSortVisible: (isVisible: boolean) => void;
    selectedSortType?: AnySortingKey;
    sortOptions?: SortOption<AnySortingKey>[];

}

export const SortButton = forwardRef<HTMLButtonElement, SortButtonProps>(({ componentClass, isSortVisible, setSortPosition, setSortVisible, selectedSortType, sortOptions }, ref) => {
    const selectedSortLabel = useMemo(
        () => sortOptions?.find((option) => option.actionType === selectedSortType)?.label ?? 'Recent', // Recent is the default option
        [selectedSortType, sortOptions]
    );
    const isSortActive = selectedSortLabel !== 'Recent';

    return (
        <button
            ref={ref}
            type="button"
            className={classNames(styles['sort-button'], { [styles['sort-button--active']]: isSortActive }, { [componentClass]: componentClass })}
            aria-label="Sort"
            data-tip={isSortVisible ? undefined : `Sort by: ${selectedSortLabel}`}
            onClick={(e) => {
                ReactTooltip.hide(e.currentTarget);
                if (isSortVisible) {
                    setSortVisible(false);
                    return;
                }
                // The sort menu is rendered as a `position: absolute` sibling elsewhere in the
                // tree, so it needs the button's position relative to their shared positioned
                // ancestor - not just the button's own (possibly `position: sticky`) parent.
                const { x, y } = getPositionRelativeToPositionedAncestor(e.currentTarget);
                setSortPosition({ left: x, top: y });
                setSortVisible(true);
            }}
        >
            <SortIcon className={classNames(styles['sort-button__icon'], isSortActive ? styles['sort-button--active'] : '')} />
        </button>
    );
});
